import _ from 'lodash'
import {
    aiRequestErrorBlacklistValidationFailedTag,
    aiRequestErrorOutlineValidationFailedTag,
    blacklistedContentPhrases
} from './constants'
import * as constants from '../../constants/constants'
import {extractTextFromHTML, findMissingKeys, insertTextInHtml, normalizeString} from './aiExtensionUtils'
import type {CoreLogger, CreateExtArgs, ExtensionAPI} from '@wix/document-manager-core'
export type PointerPredicate = (pointer: Pointer) => boolean

import type {Outline, OutlineValidationResults} from './aiContentExtension'
import {ReportableError} from '@wix/document-manager-utils'
import type {Pointer, FlatOutline} from '@wix/document-services-types'
import {DATA_TYPES, HEADER_NAME} from '../../constants/constants'
import type {StructureExtensionAPI} from '../structure'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import type {ComponentDefinitionExtensionAPI} from '../componentDefinition'

export interface OutlineValidationCounters {
    totalComponentTextCount: number
    missingComponentTextCount: number
    componentTextsWithBlacklistedPhrasesCount: number
    validComponentTextCount: number
}
export interface CompletionCallMetadata {
    textCounters?: OutlineValidationCounters
    isBadJson: boolean
}
export const unsectionedOutlineSectionName = 'unsectioned'
export const outlineCompIdSectionNameOverrides = {
    [constants.COMP_IDS.HEADER]: 'header',
    [constants.COMP_IDS.FOOTER]: 'footer'
}

const buttonComponentType = 'wysiwyg.viewer.components.SiteButton'
const styleableButtonComponentType = 'wixui.StylableButton'
export const textComponentType = 'wysiwyg.viewer.components.WRichText'
const textInputComponentType = 'wysiwyg.viewer.components.inputs.TextInput'
const marqueeComponentType = 'wixui.TextMarquee'
const textMaskType = 'wixui.TextMask'

// ignored due to not being supported
export const ignoredTemplateOutlineTypes = new Set([
    'wysiwyg.viewer.components.SiteRegionContainer',
    marqueeComponentType
])

export const serializedRepeaterDataItemType = 'RepeatedData'
export const serializedTextMarqueeDataItemType = 'TextMarqueeData'
export const serializedTextMarqueeDataTextItemType = 'TextMarqueeItem'
export const componentContentExtractionMetadata = {
    [styleableButtonComponentType]: {
        dataContentField: 'label',
        contentContainerTypes: ['button'],
        html: false
    },
    [buttonComponentType]: {
        dataContentField: 'label',
        contentContainerTypes: ['button'],
        html: false
    },
    [textComponentType]: {
        dataContentField: 'text',
        contentContainerTypes: ['title', 'paragraph'],
        html: true
    },
    [textInputComponentType]: {
        dataContentField: 'label',
        html: false
    },
    [textMaskType]: {
        dataContentField: 'text',
        html: false
    },
    [marqueeComponentType]: {
        dataContentField: 'text',
        html: false
    }
}
const blacklistedPhraseOccurrenceThreshold = 1
const ratioOfLengthTooLong = 5
export const invalidOutlineRetryAmount = 1

export const structureTypes = {
    PAGE: 'PAGE',
    SECTION: 'SECTIOM'
}
export const getSectionName = (anchorItem: Record<string, any>): string => {
    if (anchorItem?.name) {
        // convert to a readable JSON property name and filter out non-alphanumeric characters
        return _.snakeCase(anchorItem.name).replace(/[^a-z0-9_]/g, '')
    }

    return 'section'
}

const getContentValue = (componentType: string, dataItem: Record<string, any>) => {
    const value = dataItem[componentContentExtractionMetadata[componentType].dataContentField]

    if (componentContentExtractionMetadata[componentType].html) {
        return normalizeString(extractTextFromHTML(value))
    }

    return normalizeString(value)
}

const getContentMetadataTypeForComponent = (componentType: string, dataItem: Record<string, any>) => {
    if (componentType === buttonComponentType || componentType === styleableButtonComponentType) {
        return 'button'
    }

    if (componentType === textInputComponentType) {
        return 'textInput'
    }

    if (componentType === marqueeComponentType) {
        return 'textMarquee'
    }

    if (componentType === textComponentType || componentType === textMaskType) {
        const value = getContentValue(componentType, dataItem)
        if (value.includes('title')) {
            return 'title'
        }
        if (_.words(value).length < 10 && !value.includes('paragraph')) {
            return 'title'
        }

        return 'paragraph'
    }

    return undefined
}
export const getNextIdForPrefix = (prefix: string, typeCounter: Record<string, number>) => {
    const currentValue = typeCounter[prefix] || 0
    const newValue = currentValue + 1
    typeCounter[prefix] = newValue

    if (newValue === 1) {
        return prefix
    }

    return `${prefix}_${newValue}`
}
export const getContentForComponent = (
    componentType: string,
    dataItem: Record<string, any>
): {value: string; type: string} | undefined => {
    const type = getContentMetadataTypeForComponent(componentType, dataItem)

    if (type) {
        const value = getContentValue(componentType, dataItem)

        return {value, type}
    }

    return undefined
}

const countOutlineBlacklistPhraseOccurrenceForSection = (section: Record<string, any>): number => {
    let occurrences = 0
    for (const fieldName of Object.keys(section)) {
        for (const blacklistedContentPhrase of blacklistedContentPhrases) {
            const value = section[fieldName]
            if (!!value && typeof value === 'string') {
                const normalizedResultValue = value.toLowerCase()
                if (normalizedResultValue.includes(blacklistedContentPhrase)) {
                    occurrences += 1
                }
            }
        }
    }
    return occurrences
}

const countOutlineBlacklistPhraseOccurrence = (outline: Outline): number => {
    let occurrences = 0
    for (const sectionName of Object.keys(outline)) {
        const occurrencesForSection = countOutlineBlacklistPhraseOccurrenceForSection(outline[sectionName])
        occurrences += occurrencesForSection
    }

    return occurrences
}
export const validateOutlineSuggestionInternal = (
    outlineFieldsMismatch: boolean,
    totalComponentTextCount: number,
    missingComponentTextCount: number,
    componentTextsWithBlacklistedPhrasesCount: number
): OutlineValidationResults => {
    const blackListThresholdExceeded = componentTextsWithBlacklistedPhrasesCount > blacklistedPhraseOccurrenceThreshold

    return {
        isValid: !outlineFieldsMismatch && !blackListThresholdExceeded,
        outlineFieldsMismatch,
        blackListThresholdExceeded,
        counters: {
            totalComponentTextCount,
            missingComponentTextCount,
            componentTextsWithBlacklistedPhrasesCount,
            validComponentTextCount:
                totalComponentTextCount - missingComponentTextCount - componentTextsWithBlacklistedPhrasesCount
        }
    }
}

export const validateFlatOutlineSuggestion = (result: FlatOutline, outline: FlatOutline): OutlineValidationResults => {
    let outlineFieldsMismatch = false
    const totalComponentTextCount = Object.keys(outline).length
    let missingComponentTextCount = 0
    const missingContentFields = findMissingKeys(outline, result)
    if (missingContentFields.length > 0) {
        outlineFieldsMismatch = true
        missingComponentTextCount += missingContentFields.length
    }

    const componentTextsWithBlacklistedPhrasesCount = countOutlineBlacklistPhraseOccurrenceForSection(result)
    return validateOutlineSuggestionInternal(
        outlineFieldsMismatch,
        totalComponentTextCount,
        missingComponentTextCount,
        componentTextsWithBlacklistedPhrasesCount
    )
}

export const validateOutlineSuggestion = (result: Outline, outline: Outline): OutlineValidationResults => {
    let outlineFieldsMismatch = false
    const totalComponentTextCount = _.sumBy(Object.values(outline), section => Object.keys(section).length)
    let missingComponentTextCount = 0
    for (const sectionName of Object.keys(outline)) {
        const missingContentFields = findMissingKeys(outline[sectionName], result[sectionName])
        if (missingContentFields.length > 0) {
            outlineFieldsMismatch = true
            missingComponentTextCount += missingContentFields.length
        }
    }

    const componentTextsWithBlacklistedPhrasesCount = countOutlineBlacklistPhraseOccurrence(result)
    return validateOutlineSuggestionInternal(
        outlineFieldsMismatch,
        totalComponentTextCount,
        missingComponentTextCount,
        componentTextsWithBlacklistedPhrasesCount
    )
}
export const reportOutlineBIError = (
    logger: CoreLogger,
    errorType: string,
    message: string,
    result: Outline | FlatOutline,
    pageId: string,
    businessType: string,
    businessName: string,
    additionalInformation: string,
    validationResults: OutlineValidationResults
) => {
    const error = new ReportableError({
        errorType,
        message,
        tags: {
            [aiRequestErrorOutlineValidationFailedTag]: validationResults.outlineFieldsMismatch,
            [aiRequestErrorBlacklistValidationFailedTag]: validationResults.blackListThresholdExceeded
        },
        extras: {
            pageId,
            businessType,
            businessName,
            additionalInformation,
            suggestedOutline: result
        }
    })
    logger.captureError(error)
}
// A function that checks if the length of the content provided is more than 5 times the current content length
export const validateContentLengthInternal = (currentContent: string | undefined, newContent: string): boolean => {
    const currentContentLength = currentContent?.length ?? 0
    const newContentLength = newContent.length

    if (newContentLength > currentContentLength * ratioOfLengthTooLong) {
        return false
    }

    return true
}
export const updateDataItemValue = (dataItem: Record<string, any>, componentType: string, content: string) => {
    const contentExtractionMetadata = componentContentExtractionMetadata[componentType]
    if (contentExtractionMetadata.html) {
        const currentHtmlContent = dataItem[contentExtractionMetadata.dataContentField]
        content = insertTextInHtml(currentHtmlContent, content, true)
    }
    dataItem[contentExtractionMetadata.dataContentField] = content
}

export const reportImageUploadError = (logger: CoreLogger, errorType: string, message: string, extras: any) => {
    const error = new ReportableError({
        errorType,
        message,
        extras
    })
    logger.captureError(error)
}

export const reportBIError = (
    logger: CoreLogger,
    errorType: string,
    message: string,
    result: any,
    validationResults: any
) => {
    const error = new ReportableError({
        errorType,
        message,
        extras: {
            suggestedInjection: result,
            validationResults
        }
    })
    logger.captureError(error)
}

export const getSectionNameByPointer = (
    extensionAPI: ExtensionAPI,
    sectionPointer: Pointer,
    name: (typeof DATA_TYPES)['anchors' | 'naming'] = 'anchors'
): string => {
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const anchorItem = dataModel.components.getItem(sectionPointer, constants.DATA_TYPES[name])
    return getSectionName(anchorItem)
}
const getOutineContainerName = (
    extensionAPI: ExtensionAPI,
    containerPointer: Pointer,
    containerType: string
): string | undefined => {
    const {componentDefinition} = extensionAPI as ComponentDefinitionExtensionAPI
    if (componentDefinition.isSection(containerType)) {
        return getSectionNameByPointer(extensionAPI, containerPointer)
    }
    return outlineCompIdSectionNameOverrides[containerPointer.id]
}
export const getContentRecursively = (
    createExtArgs: CreateExtArgs,
    compPointer: Pointer,
    outline: Outline,
    typeCounter: Record<string, number>,
    idMap: Record<string, string>,
    outlineSectionName?: string | undefined,
    pointerPredicate?: PointerPredicate
): void => {
    const {extensionAPI, pointers} = createExtArgs
    const {components} = extensionAPI as StructureExtensionAPI
    const {componentDefinition} = extensionAPI as ComponentDefinitionExtensionAPI
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const {id} = compPointer
    const type = components.getComponentType(compPointer)

    if (ignoredTemplateOutlineTypes.has(type)) {
        return
    }

    if (componentDefinition.isContainer(type)) {
        const containerName = getOutineContainerName(extensionAPI, compPointer, type)
        const containerOutlineSectionName = containerName
            ? getNextIdForPrefix(containerName, typeCounter)
            : outlineSectionName

        if (containerOutlineSectionName && !idMap[containerOutlineSectionName]) idMap[containerOutlineSectionName] = id

        const children = pointers.structure.getChildren(compPointer)
        for (const childPointer of children) {
            getContentRecursively(
                createExtArgs,
                childPointer,
                outline,
                typeCounter,
                idMap,
                containerOutlineSectionName,
                pointerPredicate
            )
        }
    }

    if (pointerPredicate && !pointerPredicate(compPointer)) return

    const dataItem = dataModel.components.getItem(compPointer, constants.DATA_TYPES.data)
    const content = getContentForComponent(type, dataItem)
    if (content) {
        const mappedId = getNextIdForPrefix(content.type, typeCounter)
        idMap[mappedId] = id

        const outlineSectionNameOrDefault =
            outlineSectionName ??
            (pointers.structure.isInMasterPage(compPointer) ? HEADER_NAME : unsectionedOutlineSectionName)
        outline[outlineSectionNameOrDefault] ??= {}
        outline[outlineSectionNameOrDefault][mappedId] = content.value
    }
}
